/* === BEGIN imported: modalPages/passengerQuantity.jsx (verbatim from plugin, with phase-1 routing deviation) === */
/**
 * PassengerQuantity Modal Page
 * Allows users to select passenger types and quantities
 */

import React, {useState, useEffect} from 'react';
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";

// Components
import NumberCounter from '@/customComponents/uiComponents/global/NumberCounter.jsx';
import {KeenSlider} from '../Components/keenSlider.jsx';
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx';

// Services
import {tripService} from '@/services/tripService';

// Hooks
import {usePassengerTypes, useChildSeatTypes} from '@/hooks/useSelectables';

// Constants
import {MODAL_STEPS} from '@/constants/steps';
import {STORAGE_KEYS} from '@/constants/storageKeys';

// Utilities
import {storage} from '@/utils/storage';

import {smallArrowRight} from '@img/index.js';
import {ModalFooter} from '../Components/BookingModalShell.jsx';

export function PassengerQuantity({setStep, closeModal}) {
    // Reference data (cached for the session via react-query)
    const {data: passengerTypes = [], isLoading: passengerLoading} = usePassengerTypes();
    const {data: childSeatTypes = [], isLoading: childSeatLoading} = useChildSeatTypes();
    const isLoading = passengerLoading || childSeatLoading;

    // Local state
    const [passengerCounts, setPassengerCounts] = useState({});
    const [passengerError, setPassengerError] = useState(false);
    const [isRecalculating, setIsRecalculating] = useState(false);

    // Check if in edit mode (storage.get returns boolean for 'true'/'false' strings)
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true;

    // Initialize skip luggage step
    useEffect(() => {
        storage.set(STORAGE_KEYS.SKIP_LUGGAGE_STEP, 'false');
    }, []);

    // Adults are the only passenger type with a non-zero floor: a booking always
    // carries at least one, so its counter starts at 1 and its minus button can't take
    // it below. Children/Infants keep the default 0. Matched to the plugin's booking
    // portal, which does the same by label lookup (the ids come from the API).
    const adultTypeId = passengerTypes.find(
        (t) => t.label?.toLowerCase().includes('adult')
    )?.id;
    const minCountFor = (typeId) => (typeId === adultTypeId ? 1 : 0);


    /**
     * Handle passenger count change
     */
    const handleCountChange = (optionId, value) => {
        setPassengerCounts((prev) => ({...prev, [optionId]: value}));
        if (value > 0) {
            setPassengerError(false);
        }
    };

    /**
     * Validate passenger selection
     */
    const validateSelection = () => {
        const anySelected = Object.values(passengerCounts).some((count) => count > 0);
        if (!anySelected) {
            setPassengerError(true);
            return false;
        }
        return true;
    };

    /**
     * Handle continue to luggage
     */
    const handleContinue = () => {
        if (!validateSelection()) return;

        storage.set(STORAGE_KEYS.SKIP_LUGGAGE_STEP, 'false');
        setPassengerError(false);
        setStep(MODAL_STEPS.PASSENGER_LUGGAGE);
    };

    /**
     * Handle edit mode done (recalculate and return to order details)
     */
    const handleEditDone = async () => {
        if (!tripService.needsRecalculation()) {
            setStep(MODAL_STEPS.ORDER_DETAILS);
            return;
        }

        setIsRecalculating(true);

        try {
            const result = await tripService.recalculateCost();

            if (result.success) {
                setStep(MODAL_STEPS.ORDER_DETAILS);
            }
        } catch (error) {
            console.error('Recalculation error:', error);
        } finally {
            setIsRecalculating(false);
        }
    };

    const isDataReady = passengerTypes.length > 0 && childSeatTypes.length > 0;

    return (
        <>
            {/* Main Header — back closes the modal (passengerQuantity is the booking
                flow's start step), cross also closes. */}
            {/* cancelButton={false}: this is a mid-flow step with a back arrow, so the
                header was rendering BOTH a back arrow and a ✕. Matches
                travelingDateTime / passengerLuggage / specialRequest. */}
            <ModalHeader
                title="Add Passenger"
                extraClasses="first"
                totalSteps={1}
                // Shown at EVERY width. The plugin hides this on mobile because its
                // passengerLocation step sits upstream and carries the ✕ — this portal has
                // no such step (the location form lives in the banner), so passengerQuantity
                // is the first modal step and the ✕ is the only way out of it on a phone.
                cancelButton={true}
                cancelButtonType="icon"
                showBackButton={true}
                backStep={null}
                setStep={setStep}
                closeModal={closeModal}
            />

            <div className="little-title modalPages-spacing">
                <h2>Add Passenger</h2>
            </div>

            {/* Passenger Types List */}
            <div className="hopn-passenger-section modalPages-spacing">
                {isLoading && passengerTypes.length === 0 ? (
                    <center>
                        <Loader/>
                    </center>
                ) : (
                    passengerTypes.map((option) => (
                        <div key={option.id} className="hopn-quantity-item">
                            <div className="hopn-details">
                                <span className="hopn-icon-box">
                                    <img
                                        decoding="async"
                                        src={option.image_url}
                                        alt={option.label}
                                    />
                                </span>
                            </div>

                            <div className="hopn-counter">
                                <div>
                                    <span className="hopn-label">{option.label}</span>
                                    <span className="hopn-sub-label">{option.description}</span>
                                </div>
                                <div
                                    className="hopn-counter-inner"
                                    onClick={() => setPassengerError(false)}
                                >
                                    <NumberCounter
                                        id={`passengerType-${option.id}`}
                                        variant="booking"
                                        min={minCountFor(option.id)}
                                        initial={minCountFor(option.id)}
                                        onChange={(val) => handleCountChange(option.id, val)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Passenger Error Message */}
            {passengerError && (
                <div className="error-message">
                    Please select at least 1 passenger.
                </div>
            )}

            {/* Child Car Seat Section */}
            <div className="hopn-carseat-header modalPages-spacing">
                <span className="hopn-free-tag">Free</span>
                <button
                    className="hopn-found-link"
                    onClick={() => setStep(MODAL_STEPS.CHILD_SEATS_DETAILS)}
                >
                    Found right seat{' '}
                    <img src={smallArrowRight} alt="Arrow right"/>
                </button>
            </div>

            <div className="hopn-carseat-section modalPages-spacing pr-0!">
                <KeenSlider
                    items={childSeatTypes}
                    counter={true}
                    detailsView={false}
                    itemNumber="4.4"
                />
            </div>

            {/* Footer Buttons */}
            <ModalFooter className="first">
                {isEditMode ? (
                    <button
                        className="hopn-add-passenger-button no-icon"
                        disabled={isRecalculating}
                        onClick={handleEditDone}
                    >
                        {isRecalculating ? (
                            <Loader color="#fff"/>
                        ) : (
                            'Done'
                        )}
                    </button>
                ) : (
                    <button
                        className="hopn-add-passenger-button luggage"
                        onClick={handleContinue}
                        disabled={!isDataReady}
                    >
                        {isLoading ? (
                            <Loader color="#fff"/>
                        ) : (
                            '+Add Luggage'
                        )}
                    </button>
                )}
            </ModalFooter>
        </>
    );
}

export default PassengerQuantity;
/* === END imported: modalPages/passengerQuantity.jsx === */
